import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addCart, delCart } from "../redux/action";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import toast from "react-hot-toast";
import api from "../utils/base_url";
import { Navbar, Footer } from "../components";
import '../styles/index.css';
import CategorySidebar from "../components/CategorySidebar";
import StockNotifyModal from "../components/StockNotifyModal";

const CategoryProducts = () => {
    const { categoryId } = useParams();
    const [products, setProducts] = useState([]);
    const [categoryName, setCategoryName] = useState("");
    const [loading, setLoading] = useState(false);
    const [favorites, setFavorites] = useState([]);
    const [animatingHeart, setAnimatingHeart] = useState(null);
    const [stockError, setStockError] = useState({});
    const [disabledQty, setDisabledQty] = useState({});
    const [categories, setCategories] = useState([]);
    const [selectedCat, setSelectedCat] = useState(Number(categoryId));
    const [notifyModal, setNotifyModal] = useState(false);
    const [notifyProduct, setNotifyProduct] = useState(null);
    const [notifyForm, setNotifyForm] = useState({
        email: "",
        quantity: "",
    });

    const { user } = useAuth();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const state = useSelector((state) => state.handleCart);

    const getCartQty = (productId) => {
        const item = state.find((p) => p.id === productId);
        return item ? item.qty : 0;
    };

    const fetchCategories = useCallback(async () => {
        try {
            const res = await api.get("categorylist/");
            setCategories(res.data.data || []);
        } catch (error) {
            console.error("Error fetching categories:", error);
        }
    }, []);

    const fetchProducts = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get(`productfilter/${categoryId}/`);
            const data = res.data.data || [];
            data.sort((b, a) => a.id - b.id);
            setProducts(data);
            if (data.length > 0) setCategoryName(data[0].category_name || "");
        } catch (error) {
            console.error("Error fetching products:", error);
        } finally {
            setLoading(false);
        }
    }, [categoryId]);

    const fetchFavorites = useCallback(async () => {
        if (!user) return;
        try {
            const res = await api.get(`favorites/ids/?auth_id=${user.id}`);
            setFavorites(res.data.favorites || []);
        } catch (err) {
            console.error(err);
        }
    }, [user]);

    useEffect(() => {
        fetchProducts();
        fetchFavorites();
        fetchCategories();
    }, [fetchProducts, fetchFavorites, fetchCategories]);

    useEffect(() => {
        setSelectedCat(Number(categoryId));
    }, [categoryId]);

    const toggleFavorite = async (productId) => {
        if (!user) {
            toast.error("Please login first!");
            navigate("/login");
            return;
        }
        try {
            const res = await api.post("favorites/toggle/", {
                product_id: productId,
                auth_id: user.id,
            });
            const isFav = res.data.favorite;
            setFavorites((prev) =>
                isFav ? [...prev, productId] : prev.filter((id) => id !== productId)
            );
            setAnimatingHeart(productId);
            setTimeout(() => setAnimatingHeart(null), 400);
            fetchFavorites();
        } catch (err) {
            toast.error("Failed, try again");
        }
    };

    const addItem = (product) => {
        const available = product.available_stock ?? product.stock_quantity;
        const nextQty = getCartQty(product.id) + 1;
        if (nextQty > available) {
            setStockError((prev) => ({ ...prev, [product.id]: true }));
            setDisabledQty((prev) => ({ ...prev, [product.id]: true }));
            setTimeout(() => {
                setStockError((prev) => ({ ...prev, [product.id]: false }));
                setDisabledQty((prev) => ({ ...prev, [product.id]: false }));
            }, 2000);
            return;
        }
        dispatch(addCart(product));
    };

    const removeItem = (product) => {
        dispatch(delCart(product));
        setStockError((prev) => {
            const s = { ...prev };
            delete s[product.id];
            return s;
        });
    };

    const badgeStyle = {
        position: "absolute",
        top: "8px",
        left: "30%",
        transform: "translateX(-50%)",
        padding: "6px 14px",
        background: "#dc3545",
        color: "white",
        borderRadius: "20px",
        fontSize: "0.8rem",
        fontWeight: 600,
        zIndex: 99,
        whiteSpace: "nowrap",
        boxShadow: "0 3px 10px rgba(0,0,0,0.2)",
    };

    const Loading = () => (
        <div className="cp-grid">
            {[...Array(6)].map((_, idx) => (
                <div key={idx} className="cp-skeleton-card">
                    <Skeleton height={140} style={{ borderRadius: "10px" }} />
                    <Skeleton height={20} width={120} style={{ marginTop: "12px" }} />
                    <Skeleton height={12} width="90%" />
                    <Skeleton height={12} width="80%" />
                    <Skeleton height={20} width={90} style={{ marginTop: "12px" }} />
                    <Skeleton height={35} width="60%" style={{ margin: "10px auto" }} />
                </div>
            ))}
        </div>
    );

    return (
        <>
            <Navbar />
            <div style={{ backgroundColor: "#E9DCC3", minHeight: "100vh" }}>
                <div className="d-flex align-items-start cp-wrapper">

                    {/* Sidebar */}
                    <div className="cp-sidebar-follow">
                        <CategorySidebar
                            categories={categories}
                            selectedCat={selectedCat}
                            handleCategory={(id) => {
                                navigate(`/category/${id}`);
                            }}
                        />
                    </div>

                    {/* Main Content */}
                    <div className="flex-grow-1 py-4 px-3">

                        {/* Back button + Title */}
                        <div className="d-flex align-items-center gap-3 mb-4">
                            <button
                                onClick={() => navigate("/product")}
                                className="cp-back-btn"
                            >
                                ← Back
                            </button>
                            <h2 className="fw-bold text-uppercase mb-0 cp-title">
                                {categoryName || "Products"}
                            </h2>
                        </div>

                        {loading ? (
                            <Loading />
                        ) : products.length === 0 ? (
                            <div className="text-center py-5" style={{ color: "#8A4B27" }}>
                                <h5>No products available in this category.</h5>
                                <button
                                    onClick={() => navigate("/products")}
                                    className="cp-browse-btn mt-3"
                                >
                                    Browse All Products
                                </button>
                            </div>

                        ) : (
                            <div className="cp-grid">
                                {products.map((product, idx) => {
                                    const hasOffer =
                                        product.offer_percentage &&
                                        Number(product.offer_percentage) > 0 &&
                                        product.offer_price;
                                    const available = product.available_stock ?? product.stock_quantity;
                                    const outOfStock = available === 0;
                                    const stockLow = available <= 2 && available > 0;

                                    return (
                                        <div
                                            key={product.id}
                                            className="cp-card"
                                            style={{
                                                opacity: outOfStock ? 0.6 : 1,
                                                cursor: "pointer",
                                            }}
                                            onClick={() => navigate(`/product/${product.id}`)}
                                        >
                                            {hasOffer && (
                                                <span className="cp-offer-badge">
                                                    {Math.round(product.offer_percentage)}% OFF
                                                </span>
                                            )}
                                            {outOfStock && (
                                                <span className="cp-stock-badge cp-out-stock">Out of Stock</span>
                                            )}
                                            {!outOfStock && stockLow && (
                                                <span className="cp-stock-badge cp-low-stock">
                                                    Hurry! Only {available} left
                                                </span>
                                            )}
                                            {stockError[product.id] && (
                                                <motion.div
                                                    initial={{ x: 100, opacity: 0 }}
                                                    animate={{ x: [100, 0, -6, 6, -6, 6, 0], opacity: 1 }}
                                                    transition={{ duration: 0.6, ease: "easeOut" }}
                                                    style={badgeStyle}
                                                >
                                                    Stock limit reached!
                                                </motion.div>
                                            )}

                                            {/* Image */}
                                            <div className="cp-img-wrapper">
                                                <img src={product.product_image} alt={product.product_name} />
                                            </div>

                                            {/* Content */}
                                            <div className="cp-content">
                                                <h5 className="cp-title-text">
                                                    {product.product_name.length > 15
                                                        ? product.product_name.substring(0, 15) + "..."
                                                        : product.product_name}
                                                </h5>

                                                {/* Favorite */}
                                                <motion.div
                                                    onClick={(e) => { e.stopPropagation(); toggleFavorite(product.id); }}
                                                    initial={{ scale: 1 }}
                                                    whileTap={{ scale: 0.9 }}
                                                    style={{ position: "absolute", top: "10px", right: "10px", cursor: "pointer", zIndex: 50 }}
                                                >
                                                    {user && (
                                                        <div className="cp-heart-btn">
                                                            {favorites.includes(product.id) ? (
                                                                <motion.span
                                                                    animate={animatingHeart === product.id ? { scale: [1, 1.4, 1] } : {}}
                                                                    transition={{ duration: 0.3 }}
                                                                    style={{ fontSize: "12px" }}
                                                                >❤️</motion.span>
                                                            ) : (
                                                                <span style={{ color: "#4A1F1A", fontSize: "12px" }}>🤍</span>
                                                            )}
                                                        </div>
                                                    )}
                                                </motion.div>

                                                {/* Price */}
                                                <div className="cp-price-wrap">
                                                    {hasOffer ? (
                                                        <>
                                                            <span className="cp-price-offer">
                                                                ₹ {parseFloat(product.offer_price).toLocaleString()}
                                                            </span>
                                                            <span className="cp-price-original">
                                                                <s>₹ {parseFloat(product.price).toLocaleString()}</s>
                                                            </span>
                                                        </>
                                                    ) : (
                                                        <span className="cp-price">
                                                            ₹ {parseFloat(product.price).toLocaleString()}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Buttons */}
                                            <div className="product-btns">
                                                <div className="product-btns" onClick={(e) => e.stopPropagation()}>
                                                    {product.stock_quantity === 0 ? (
                                                        <div
                                                            style={{
                                                                display: "flex",
                                                                gap: "8px",
                                                                alignItems: "center",
                                                            }}
                                                        >
                                                            <button
                                                                className="notify-btn"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setNotifyProduct(product);
                                                                    setNotifyForm({
                                                                        email: "",
                                                                        quantity: "",
                                                                    });
                                                                    setNotifyModal(true);
                                                                }}
                                                            >
                                                                <span style={{ fontSize: "16px" }}>🔔</span>
                                                                Notify Me
                                                            </button>
                                                        </div>
                                                    ) : getCartQty(product.id) === 0 ? (
                                                        <button
                                                            className="btn-buy"
                                                            onClick={() => addItem(product)}
                                                        >
                                                            Add to Cart
                                                        </button>
                                                    ) : (
                                                        <div className="qty-box">
                                                            <button className="qty-btn" onClick={() => removeItem(product)}>
                                                                <i className="fas fa-minus"></i>
                                                            </button>
                                                            <span className="qty-value">{getCartQty(product.id)}</span>
                                                            <button
                                                                className="qty-btn"
                                                                onClick={() => addItem(product)}
                                                                disabled={disabledQty[product.id]}
                                                            >
                                                                <i className="fas fa-plus"></i>
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        {/* 🔥 Bottom Back Button */}
                        <div style={{ textAlign: "center", marginTop: "30px" }}>
                            <button
                                onClick={() => navigate("/product")}
                                className="cp-back-btn"
                            >
                                ← Back to Other Products
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />

            <style>{`
                /* Layout */
                .cp-wrapper {
                    gap: 0;
                }

                /* Sidebar sticky */
                .cp-sidebar-follow {
                    position: sticky;
                    top: 80px;
                    align-self: flex-start;
                    z-index: 10;
                }
                @media (max-width: 992px) {
                    .cp-sidebar-follow { display: none; }
                }

                /* Back button */
                .cp-back-btn {
                    background: none;
                    border: 1.5px solid #4A1F1A;
                    border-radius: 8px;
                    padding: 6px 14px;
                    color: #4A1F1A;
                    font-weight: 600;
                    cursor: pointer;
                    font-size: 0.85rem;
                    transition: all 0.2s ease;
                }
                .cp-back-btn:hover {
                    background: #4A1F1A;
                    color: #fff;
                }

                /* Page title */
                .cp-title {
                    color: #4A1F1A;
                    font-size: 1.4rem;
                }

                /* Browse button */
                .cp-browse-btn {
                    background: #4A1F1A;
                    color: #fff;
                    border: none;
                    border-radius: 8px;
                    padding: 8px 20px;
                    cursor: pointer;
                    font-weight: 600;
                    transition: background 0.2s ease;
                }
                .cp-browse-btn:hover { background: #8A4B27; }

                /* Grid */
                .cp-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 18px;
  padding: 0 10px;
}

/* Tablet */
@media (min-width: 768px) {
  .cp-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

/* Laptop */
@media (min-width: 1024px) {
  .cp-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

/* Desktop (🔥 FINAL 6 GRID) */
@media (min-width: 1400px) {
  .cp-grid {
    grid-template-columns: repeat(6, 1fr);
  }
}
                /* Skeleton card */
                .cp-skeleton-card {
                    background: #E9DCC3;
                    border-radius: 12px;
                    border: 1.5px solid #8A4B27;
                    height: 320px;
                    padding: 12px;
                    display: flex;
                    flex-direction: column;
                    justify-content: flex-start;
                    gap: 8px;
                }

                /* Product card */
                .cp-card {
                    position: relative;
                    background: #E9DCC3;
                    border-radius: 18px;
                    border: 2px solid #6d4c41;
                    padding: 10px;
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                    height: 320px;
                    transition: all 0.3s ease;
                }
                .cp-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 8px 20px rgba(0,0,0,0.12);
                }
                @media (max-width: 767px) {
                    .cp-card { height: auto; }
                }

                /* Badges */
                .cp-offer-badge {
                    position: absolute;
                    top: 140px;
                    left: 0;
                    background: linear-gradient(135deg, #567a0f, #2E7D32);
                    color: #fff;
                    padding: 2px 8px;
                    font-size: 0.75rem;
                    font-weight: 600;
                    border-radius: 0 6px 6px 0;
                    box-shadow: 0 2px 5px rgba(0,0,0,0.15);
                    z-index: 10;
                }
                .cp-stock-badge {
                    position: absolute;
                    top: 10px;
                    left: 0;
                    padding: 4px 10px;
                    font-size: 0.75rem;
                    font-weight: 600;
                    border-radius: 0 6px 6px 0;
                    box-shadow: 0 1px 4px rgba(0,0,0,0.13);
                    z-index: 6;
                }
                .cp-low-stock { background: #ffc107; color: #000; }
                .cp-out-stock { background: #dc3545; color: #fff; }

                /* Image */
                .cp-img-wrapper {
                    width: 100%;
                    height: 180px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    overflow: hidden;
                }
                .cp-img-wrapper img {
                    width: 80%;
                    height: 100%;
                    object-fit: contain;
                    transition: transform 0.3s ease;
                }
                .cp-card:hover .cp-img-wrapper img {
                    transform: scale(1.05);
                }

                /* Content */
                .cp-content {
                    text-align: center;
                    padding: 4px 8px;
                }
                .cp-title-text {
                    font-weight: 600;
                    font-size: 1rem;
                    color: #000;
                    margin-bottom: 4px;
                }

                /* Heart */
                .cp-heart-btn {
                    width: 26px;
                    height: 26px;
                    border-radius: 50%;
                    background-color: #E9DCC3;
                    box-shadow: 0 2px 6px #4A1F1A;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.25s ease;
                    backdropFilter: blur(3px);
                }
                .cp-heart-btn:hover { transform: scale(1.1); }

                /* Price */
                .cp-price-wrap {
                    display: flex;
                    align-items: baseline;
                    justify-content: center;
                    gap: 8px;
                    margin-top: 4px;
                    white-space: nowrap;
                }
                .cp-price        { color: #000; font-weight: bold; font-size: 1rem; }
                .cp-price-offer  { color: #4A1F1A; font-size: 1.1rem; font-weight: 700; }
                .cp-price-original { color: #dc3545; font-size: 0.88rem; }

                /* Buttons area */
                .cp-btns {
                    display: flex;
                    justify-content: center;
                    gap: 8px;
                    padding-bottom: 8px;
                }

                .cp-btn-buy {
                    background-color: #4A1F1A;
                    color: #fff;
                    border: none;
                    border-radius: 8px;
                    padding: 5px 14px;
                    font-size: 0.8rem;
                    font-weight: 600;
                    cursor: pointer;
                    transition: background 0.3s ease;
                }
                .cp-btn-buy:hover { background-color: #8A4B27; }
                .cp-btn-buy:disabled {
                    background-color: #ddd !important;
                    color: #888 !important;
                    cursor: not-allowed;
                }

                /* Qty box */
                .cp-qty-box {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    background: #fff;
                    border: 1.5px solid #4A1F1A;
                    border-radius: 8px;
                    padding: 4px 10px;
                }
                .cp-qty-btn {
                    background: #8A4B27;
                    color: #fff;
                    border: none;
                    width: 26px;
                    height: 26px;
                    border-radius: 50%;
                    font-size: 0.85rem;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: background 0.2s ease;
                }
                .cp-qty-btn:hover { background: #4A1F1A; }
                .cp-qty-btn:disabled { background: #ccc; cursor: not-allowed; }
                .cp-qty-value {
                    font-weight: 700;
                    font-size: 1rem;
                    color: #4A1F1A;
                    min-width: 18px;
                    text-align: center;
                }

                /* Out of stock label */
                .cp-out-stock-label {
                    background: #dc3545;
                    color: #fff;
                    padding: 5px 12px;
                    border-radius: 8px;
                    font-weight: 600;
                    font-size: 0.8rem;
                    display: inline-block;
                }
            `}</style>

            <StockNotifyModal
                open={notifyModal}
                onClose={() => setNotifyModal(false)}
                product={notifyProduct}
                notifyForm={notifyForm}
                setNotifyForm={setNotifyForm}
                navigate={navigate}
            />
        </>
    );
};

export default CategoryProducts;